import { Box, Flex, Text } from '@chakra-ui/react';
import { Button, OutlineCard, useToast } from '@morressier/alembic';
import { IDecisionModalProps } from '../decisionModalHelper';
import { useOfferTransfer } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDeadlineDate } from 'src/utils/date';
import { Overlay } from 'src/components/Overlay';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { JSONSchemaType } from 'ajv';
import { FormFieldTargetJournals as IFormFieldCheckbox } from '@publishing-engine/domain/types/article';
import { getSubmissionTransferNetwork } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import ReviewSummaryAccordion from '../ReviewSummaries';
import { createEditorialModal } from '../EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from '../EditorialNote';
import { useSteps } from '../hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import StepHeader from '../../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import ComposeDecisionEmail, {
  EmailContentForm
} from '../ComposeDecisionEmail';
import { buildEmailPayload } from '../../../../utils/normalizeEmailRecipients';
import AdjudicatorReviewSummary from '../AdjudicatorReviewSummary';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useAttachmentSync } from '../../../../hooks/useAttachmentSync';
import DecisionLayout from '../DecisionLayout';
import { useEffect } from 'react';
import SelectArticleTypes from './SelectArticleTypes';
import { IGetSubmissionTransferNetworkResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

type OfferTransferFormData = {
  transferReason?: string;
  targetJournals?: [{ id: string; articleTypeCategoryId: string }];
};

const roleContext = 'editorial';

export const OfferTransferModal = createEditorialModal(
  ({ onClose, submissionId, submissionContextId }: IDecisionModalProps) => {
    const toast = useToast();

    const {
      workflowResponse: {
        forms: { editorialOfferTransfer },
        articleWorkflow: { id: articleWorkflowId }
      },
      refetchSubmission,
      reviewsSummary,
      adjudicatorReviewsSummary,
      organizationId,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'EditorialOfferTransfer'
    });

    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );

    const { data: transferNetworks } = useQuery({
      queryKey: ['submission-transfer-network', { submissionId, roleContext }],
      queryFn: () => getSubmissionTransferNetwork(submissionId, roleContext)
    });
    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);

    const offerTransferMutation = useOfferTransfer({
      submissionId,
      organizationId,
      submissionContextId,
      articleWorkflowId,
      invalidateSubmissionQueries: refetchSubmission,
      emails: buildEmailPayload(emailContent)
    });

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const { formFields, schema } = editorialOfferTransfer || {};

    const fields = Object.values(formFields || {}).reduce(
      (acc, val) => ({
        ...acc,
        [val.name]: val as CustomFormFieldDefinition
      }),
      {} as Record<string, CustomFormFieldDefinition>
    );

    const methods = useForm<OfferTransferFormData>({
      defaultValues: {
        targetJournals: []
      },
      resolver: ajvResolver(schema as JSONSchemaType<OfferTransferFormData>)
    });
    const transferReason = methods.watch('transferReason');

    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const { handleSubmit, getValues } = methods;

    const onSubmit = async () => {
      const values = getValues();
      const res = await offerTransferMutation.mutateAsync(values);
      if (res.isSuccess) {
        await submitEditorNote(editorNote);
        const deadline = res.submission.workflowData.currentDeadline;
        onClose();
        toast({
          variant: 'success',
          title: 'Transfer Offered to Author',
          description: `This Submission will be rejected if the Author declines the offer or doesn’t respond${
            deadline ? ` by ${formatDeadlineDate(deadline)}` : ''
          }.`,
          isClosable: true
        });
      } else {
        toast({
          variant: 'error',
          title: 'Error offering transfer',
          description:
            res.message?.errors
              ?.map((err: { reason: string }) => err.reason)
              .join(', ') || 'Try again later!'
        });
      }
    };

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        onSubmit();
      }
    };

    const renderFirstStep = () => (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitFirstStep)}>
          <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
            <Box py={5} padding={4}>
              <StepHeader
                currentStep={currentStep}
                totalSteps={hasCustomizableTemplates ? 2 : 0}
                heading="Reject and Transfer"
                description="Prepare the transfer offer by drafting your response to
                      the author and selecting eligible journals. Your response
                      will include the reason for the transfer, additional
                      feedback, and any selected reviewer comments."
                marginY={2}
              />
            </Box>
            <Flex padding={8} paddingTop={1} gap={6} flexDirection="column">
              <Text textStyle="headingGroup">Communications to Author</Text>

              <OutlineCard borderless padding={6} width="100%">
                <Flex flexDirection="column" gap={6}>
                  {fields.TransferReason && (
                    <CustomFormFieldComponent
                      config={fields.TransferReason}
                      placeholder="Select a reason for this rejection"
                    />
                  )}
                  {fields.EditorComment && (
                    <CustomFormFieldComponent
                      config={fields.EditorComment}
                      placeholder="Add feedback to the author"
                    />
                  )}

                  <Flex direction="column" my={4}>
                    <ReviewSummaryAccordion reviews={reviewsSummary ?? []} submissionId={submissionContextId} />
                    <AdjudicatorReviewSummary
                      adjudicatorReview={adjudicatorReviewsSummary ?? []}
                    />
                  </Flex>
                </Flex>
              </OutlineCard>

              <Text textStyle="headingGroup">Transfer Offers</Text>

              {/* {fields.TargetJournals && (
                <CustomFormFieldComponent
                  config={fields.TargetJournals}
                  options={transferNetworks?.map(
                    ({ id, journalName, publishingModel }) => ({
                      id,
                      main: journalName,
                      secondary: `${journalPublishingModels[publishingModel]} Journal`
                    })
                  )}
                />
              )} */}
              <SelectArticleTypes
                config={fields.TargetJournals as IFormFieldCheckbox}
                options={
                  transferNetworks as IGetSubmissionTransferNetworkResponse
                }
              />
              <EditorialNote value={editorNote} onChange={setEditorNote} />
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    );

    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form onSubmit={emailContentMethods.handleSubmit(onSubmit)}>
          <Box marginX="auto" py={8} padding={4}>
            <StepHeader
              currentStep={currentStep}
              totalSteps={hasCustomizableTemplates ? 2 : 0}
              heading="Send Email Notification"
              description="Compose an email to notify the author about the transfer offer."
              marginY={2}
            />
          </Box>
          <ComposeDecisionEmail
            organizationId={organizationId}
            submissionContextId={submissionContextId}
            commandName="EditorialOfferTransfer"
            formPayload={methods.getValues()}
            subtype={transferReason}
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(onSubmit)()}
                isLoading={
                  !hasCustomizableTemplates && offerTransferMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() => emailContentMethods.handleSubmit(onSubmit)()}
            isLoading={offerTransferMutation.isPending}
          >
            Reject and Transfer
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout manuscriptId={manuscriptId} onClose={onClose}>
          {currentStep === 1
            ? renderFirstStep()
            : hasCustomizableTemplates
            ? renderEmailStep()
            : null}
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
